// C:\code\Website\Orion Intelligence\orion-site\src\components\IppokamposPage.jsx
// @ts-nocheck
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
    Waves,
    Users,
    Heart,
    FileText,
    PieChart,
    Shield,
    ArrowRight,
    Sparkles,
} from "lucide-react";

// Hippo/Sea Theme: Deep Teals, Ocean Blues, and Coral accents
const colors = {
    bgTop: "#0a192f",       // Deep navy/teal
    bgBottom: "#061221",    // Darker ocean
    primary: "#64ffda",     // Bright Aquamarine
    primaryHover: "#4cd9b9",
    accent: "#ff7f50",      // Coral
    cardBg: "rgba(100, 255, 218, 0.05)",
    cardBorder: "rgba(100, 255, 218, 0.2)",
    text: "#ccd6f6",        // Light slate
    textMuted: "#8892b0",   // Muted slate
};

export default function IppokamposPage() {
    useEffect(() => {
        document.title = "Ippokampos — Πλατφόρμα Διαχείρισης Συλλόγων | Orion Intelligence";
        return () => {
            document.title = "Orion Intelligence";
        };
    }, []);

    const features = [
        { 
            icon: Waves, 
            title: "Μέλη & Μητρώο",
            text: "Κεντρική διαχείριση μελών, στοιχείων επικοινωνίας, κατάστασης εγγραφής και των εγγράφων που χρειάζεται κάθε οργανισμός."
        },
        { 
            icon: Users, 
            title: "Συνδρομές & Οικονομική Εικόνα",
            text: "Παρακολούθηση συνδρομών, επιβεβαιώσεων και εκκρεμοτήτων, με καθαρή εικόνα για τους εξουσιοδοτημένους ρόλους."
        },
        { 
            icon: FileText, 
            title: "Προγράμματα & Δραστηριότητες",
            text: "Οργάνωση τμημάτων, ωραρίων, υπευθύνων και επαναλαμβανόμενων δραστηριοτήτων, προσαρμοσμένων στον τρόπο λειτουργίας του συλλόγου."
        },
        { 
            icon: Heart, 
            title: "Παρουσίες & Συμμετοχή",
            text: "Γρήγορη καταγραφή παρουσιών και ιστορικό συμμετοχής ανά μέλος, πρόγραμμα ή περίοδο."
        },
        { 
            icon: PieChart, 
            title: "Έγγραφα & Επικοινωνία",
            text: "Συγκέντρωση εγγράφων, ανακοινώσεων και βασικής επικοινωνίας ώστε η ομάδα να εργάζεται από κοινή, ενημερωμένη πληροφορία."
        },
        { 
            icon: Shield, 
            title: "Ρόλοι, Ασφάλεια & Αναφορές",
            text: "Διαβαθμισμένη πρόσβαση, λειτουργικές αναφορές και σχεδιασμός με αρχές ελαχιστοποίησης δεδομένων και προστασίας της ιδιωτικότητας."
        }
    ];

    return (
        <div
            className="min-h-screen flex flex-col relative overflow-x-hidden"
            style={{
                color: colors.text,
                background: `radial-gradient(ellipse at 20% 0%, rgba(100, 255, 218, 0.15), transparent 50%),
                             radial-gradient(ellipse at 80% 100%, rgba(255, 127, 80, 0.05), transparent 40%),
                             linear-gradient(170deg, ${colors.bgTop}, ${colors.bgBottom})`,
            }}
        >
            {/* Nav / Back to Home */}
            <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-3"
                >
                    <img
                        src="/assets/orion-logo-800.png"
                        alt="Orion logo"
                        className="w-10 h-10 rounded-xl"
                        style={{ boxShadow: `0 0 20px rgba(100, 255, 218, 0.3)` }}
                    />
                    <div className="text-left leading-tight">
                        <div className="text-lg font-bold tracking-wide" style={{ color: colors.primary }}>
                            Orion Intelligence
                        </div>
                        <div className="text-xs" style={{ color: colors.textMuted }}>Solutions for Associations</div>
                    </div>
                </motion.div>
                <a href="/" className="text-sm font-medium hover:text-white transition-colors" style={{ color: colors.textMuted }}>
                    ← Πίσω στην αρχική
                </a>
            </div>

            <main className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow relative z-10">
                {/* Hero Section */}
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
                            style={{ borderColor: colors.primary, background: "rgba(100, 255, 218, 0.05)", color: colors.primary }}>
                            <Sparkles size={16} />
                            <span className="text-xs font-bold uppercase tracking-widest">Platform for Associations</span>
                        </div>
                        
                        <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
                            Ippokampos <br />
                            <span style={{ color: colors.primary }}>Διαχείριση συλλόγων, απλά και οργανωμένα</span>
                        </h1>
                        
                        <p className="text-lg md:text-xl leading-relaxed mb-8" style={{ color: colors.textMuted }}>
                            Γενική πλατφόρμα για συλλόγους, σωματεία και οργανισμούς μελών. Συνδέει μητρώο,
                            συνδρομές, προγράμματα, παρουσίες, έγγραφα και αναφορές σε ένα ασφαλές περιβάλλον,
                            προσαρμοσμένο στις πραγματικές διαδικασίες κάθε φορέα.
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <a href="/#contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold transition-all shadow-lg"
                                style={{ background: colors.primary, color: colors.bgTop }}>
                                Συζητήστε τη δική σας υλοποίηση <ArrowRight size={18} />
                            </a>
                            <div className="px-5 py-4 rounded-2xl border" style={{ borderColor: colors.cardBorder }}>
                                <span className="text-sm font-bold opacity-80">Προσαρμόσιμη πλατφόρμα</span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-cyan-500/10 blur-[120px] rounded-full" />
                        <div className="relative rounded-[2rem] overflow-hidden border p-2 backdrop-blur-xl bg-white/5 shadow-2xl" style={{ borderColor: colors.cardBorder }}>
                            <img
                                src="/assets/ippokampos-app.png"
                                alt="Ippokampos App Screenshot"
                                className="w-full rounded-[1.5rem] object-cover"
                                style={{ display: "block" }}
                            />
                        </div>
                    </motion.div>
                </div>

                {/* Features Grid */}
                <div className="mb-16">
                    <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">Modules & Δυνατότητες</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((f, i) => (
                            <motion.div
                                key={i}
                                whileHover={{ y: -8, backgroundColor: "rgba(100, 255, 218, 0.08)" }}
                                className="p-8 rounded-[2rem] border transition-all duration-300"
                                style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
                            >
                                <div className="p-3 rounded-2xl bg-white/5 w-fit mb-6">
                                    <f.icon size={28} style={{ color: colors.primary }} />
                                </div>
                                <h3 className="text-xl font-bold mb-3">{f.title}</h3>
                                <p className="text-sm leading-relaxed" style={{ color: colors.textMuted }}>{f.text}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Live implementation / case study */}
                <div className="p-6 sm:p-10 rounded-[2rem] border bg-white/[0.02] text-center relative overflow-hidden" style={{ borderColor: colors.cardBorder }}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 blur-[80px]" />
                    <div className="relative z-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border mb-5 text-xs font-bold uppercase tracking-widest" style={{ borderColor: colors.accent, color: colors.accent }}>
                            Πραγματική υλοποίηση / Case study
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-6">Σύλλογος Ιππόκαμπος ΑμεΑ</h2>
                        <p className="max-w-2xl mx-auto text-lg mb-10" style={{ color: colors.textMuted }}>
                            Ο Σύλλογος Ιππόκαμπος ΑμεΑ αποτελεί πραγματική εφαρμογή της πλατφόρμας σε ειδικό λειτουργικό περιβάλλον.
                            Η υλοποίηση δείχνει πώς ο ίδιος πυρήνας μπορεί να προσαρμοστεί στις διαδικασίες, τα προγράμματα και τους ρόλους ενός συγκεκριμένου φορέα.
                        </p>
                        <a href="https://ippokampos-amea.gr" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold border hover:bg-white/5 transition-colors"
                            style={{ borderColor: colors.primary, color: colors.primary }}>
                            Επισκεφθείτε το ippokampos-amea.gr <ArrowRight size={18} />
                        </a>
                    </div>
                </div>
            </main>

            <footer className="py-12 border-t border-white/5 mt-20 opacity-60 text-center">
                <p className="text-xs uppercase tracking-widest">© {new Date().getFullYear()} Orion Intelligence • Ippokampos Solutions</p>
            </footer>
        </div>
    );
}
